// Вставка рисунков без ожидания: узел рисунка появляется сразу (превью из файла, «Загрузка…»), файл уходит
// на сервер в фоне; по готовности у узла появляется src (без отдельного шага отмены). SVG переводится в PNG
// в браузере (Word и writer SVG не принимают). Сведения об изображениях (размер, dpi) кэшируются.
import { useSyncExternalStore } from 'react';
import type { Editor, JSONContent } from '@tiptap/core';
import type { AssetInfo } from '../../../core/types';
import type { Ctx } from '../../../core/registry';
import { api } from '../../../core/api';
import { appStore } from '../../../core/store';
import { insertBlocks, focusInside } from '../../../core/editor/insert';
import { IMAGE_EXT, IMAGE_MIME, uploadImage } from '../../../core/assets';
import { newId } from '../../../core/schema/util';
import { TextSelection } from '@tiptap/pm/state';
import { captionFromFilename, cmToWidth, findById, naturalCm, plural, TEXT_W_CM } from '../util';

// ------------------------------------------------------------------------------------------------ превью загрузок
export interface Pending { url: string; error?: string; progress?: string }
const pending = new Map<string, Pending>();
const listeners = new Set<() => void>();
let ver = 0;
const emit = () => { ver++; listeners.forEach((f) => f()); };
export function usePending(id: string | null | undefined): Pending | undefined {
  useSyncExternalStore((f) => { listeners.add(f); return () => { listeners.delete(f); }; }, () => ver);
  return id ? pending.get(id) : undefined;
}
/** Загрузка по ключу (часть составного рисунка: `${id}#${k}`) - вместе с usePending того же рисунка. */
export function pendingOf(key: string): Pending | undefined {
  return pending.get(key);
}

// ------------------------------------------------------------------------------------------------ сведения об изображениях
const infoCache = new Map<string, AssetInfo | Promise<AssetInfo | null> | null>();
export function assetInfo(name: string): AssetInfo | null | undefined {
  const pid = appStore.getState().projectId;
  if (!pid || !name) return null;
  const v = infoCache.get(pid + '/' + name);
  if (v === undefined) {
    const p = api.get<AssetInfo>(`/api/projects/${encodeURIComponent(pid)}/assets/${encodeURIComponent(name)}/info`)
      .then((a) => { infoCache.set(pid + '/' + name, a); emit(); return a; })
      .catch(() => { infoCache.set(pid + '/' + name, null); emit(); return null; });
    infoCache.set(pid + '/' + name, p);
    return undefined;
  }
  if (v && typeof (v as Promise<unknown>).then === 'function') return undefined;
  return v as AssetInfo | null;
}
export function useAssetInfo(name: string): AssetInfo | null | undefined {
  useSyncExternalStore((f) => { listeners.add(f); return () => { listeners.delete(f); }; }, () => ver);
  return assetInfo(name);
}
export function rememberInfo(a: AssetInfo) {
  const pid = appStore.getState().projectId;
  if (pid) { infoCache.set(pid + '/' + a.name, a); emit(); }
}

// ------------------------------------------------------------------------------------------------ файлы
export const isSvg = (f: File) => f.type === 'image/svg+xml' || /\.svg$/i.test(f.name);
export const isImage = (f: File) => IMAGE_MIME.test(f.type) || IMAGE_EXT.test(f.name) || isSvg(f) || /^image\//.test(f.type);

/** SVG -> PNG (в 3 раза крупнее для чёткости печати) + ширина в см по исходному размеру SVG. */
async function svgToPng(f: File): Promise<{ blob: Blob; widthCm: number }> {
  const url = URL.createObjectURL(f);
  try {
    const img = new Image();
    img.src = url;
    await img.decode();
    const w = img.naturalWidth || 800; const h = img.naturalHeight || 600;
    const k = Math.max(1, Math.min(4, 2400 / w, 3));
    const cv = document.createElement('canvas');
    cv.width = Math.round(w * k); cv.height = Math.round(h * k);
    const g = cv.getContext('2d')!;
    g.fillStyle = '#fff'; g.fillRect(0, 0, cv.width, cv.height);
    g.drawImage(img, 0, 0, cv.width, cv.height);
    const blob: Blob = await new Promise((res, rej) => cv.toBlob((b) => (b ? res(b) : rej(new Error('SVG не удалось преобразовать'))), 'image/png'));
    return { blob, widthCm: Math.min(TEXT_W_CM, naturalCm(w)) };
  } finally {
    URL.revokeObjectURL(url);
  }
}

/** Загрузить файл (SVG - после перевода в PNG). */
export async function uploadFile(f: File): Promise<{ a: AssetInfo; width: string | null }> {
  if (isSvg(f)) {
    const { blob, widthCm } = await svgToPng(f);
    const a = await uploadImage(blob, f.name.replace(/\.svg$/i, '.png'));
    rememberInfo(a);
    return { a, width: cmToWidth(widthCm) };
  }
  const a = await uploadImage(f, f.name);
  rememberInfo(a);
  return { a, width: null };
}

// ------------------------------------------------------------------------------------------------ вставка
export interface InsertOpts { at?: number | null; captions?: boolean; select?: boolean }

/** Вставить рисунки из файлов: каждый файл - отдельный рисунок с подписью; курсор - в название первого. */
export async function insertImageFiles(ctx: Ctx, files: File[], opts: InsertOpts = {}): Promise<void> {
  const ed = ctx.editor;
  if (!ed) return;
  const imgs = files.filter(isImage);
  if (!imgs.length) { ctx.toast('Это не изображение (подходят PNG, JPEG, GIF, BMP, TIFF, WEBP, SVG)', 'warn'); return; }
  const items = imgs.map((f) => ({ f, id: newId(), caption: opts.captions ? captionFromFilename(f.name) : '' }));
  const nodes: JSONContent[] = items.map(({ f, id, caption }) => {
    pending.set(id, { url: URL.createObjectURL(f), progress: 'Загрузка…' });
    return { type: 'figure', attrs: { id, src: '', width: null, alt: '' },
      content: [{ type: 'caption', content: caption ? [{ type: 'text', text: caption }] : [] }] };
  });
  emit();
  const pos = insertBlocks(ed, nodes, opts.at ?? undefined);
  focusCaption(ed, pos, !!items[0].caption);
  if (items.length > 1) ctx.toast(`Вставлено ${items.length} ${plural(items.length, 'рисунок', 'рисунка', 'рисунков')} - каждому своя подпись`, 'ok', 3000);
  // загрузка: по 3 параллельно
  let k = 0;
  const worker = async () => {
    while (k < items.length) {
      const it = items[k++];
      try {
        const { a, width } = await uploadFile(it.f);
        finish(ed, it.id, { src: a.name, ...(width ? { width } : {}) });
      } catch (e: any) {
        fail(ed, ctx, it.id, e?.message || String(e));
      }
    }
  };
  await Promise.all([worker(), worker(), worker()]);
}

/** Курсор в название рисунка (если оно предложено по имени файла - выделить, чтобы сразу перепечатать). */
export function focusCaption(ed: Editor, pos: number, selectAll: boolean) {
  focusInside(ed, pos);
  if (!selectAll) return;
  const $p = ed.state.selection.$from;
  if ($p.parent.type.name === 'caption' && $p.parent.content.size) {
    const start = $p.start();
    ed.view.dispatch(ed.state.tr.setSelection(TextSelection.create(ed.state.doc, start, start + $p.parent.content.size)));
  }
}

function finish(ed: Editor, id: string, attrs: Record<string, unknown>) {
  const p = pending.get(id);
  pending.delete(id);
  emit();
  if (p) setTimeout(() => URL.revokeObjectURL(p.url), 5000);
  if (ed.isDestroyed) return;
  doneAttrs.set(id, attrs);
  watchRedo(ed);
  const f = findById(ed.state.doc, id);
  if (!f) return;
  ed.view.dispatch(ed.state.tr.setNodeMarkup(f.pos, undefined, { ...f.node.attrs, ...attrs }).setMeta('addToHistory', false));
}

// адрес файла ставится после загрузки вне истории отмены; Ctrl+Z, затем Ctrl+Y вернули бы рисунок без картинки -
// поэтому загруженные адреса запоминаются и подставляются, когда рисунок с тем же id снова появился пустым
const doneAttrs = new Map<string, Record<string, unknown>>();
const watched = new WeakSet<Editor>();
function watchRedo(ed: Editor) {
  if (watched.has(ed)) return;
  watched.add(ed);
  const fill = () => {
    if (ed.isDestroyed || !doneAttrs.size) return;
    const tr = ed.state.tr;
    ed.state.doc.descendants((n, p) => {
      if (n.type.name === 'figure' && !n.attrs.src && doneAttrs.has(n.attrs.id) && !pending.has(n.attrs.id)) {
        tr.setNodeMarkup(p, undefined, { ...n.attrs, ...doneAttrs.get(n.attrs.id) });
      }
      return n.type.name !== 'figure';
    });
    if (tr.docChanged) ed.view.dispatch(tr.setMeta('addToHistory', false));
  };
  // только после отмены/повтора (мета prosemirror-history) - обычный набор не обходит документ
  ed.on('update', ({ transaction }) => { if (transaction.getMeta('history$')) setTimeout(fill, 0); });
}

function fail(ed: Editor, ctx: Ctx, id: string, msg: string) {
  const p = pending.get(id);
  pending.delete(id);
  emit();
  if (p) URL.revokeObjectURL(p.url);
  ctx.toast('Рисунок не вставлен: ' + msg, 'error', 7000);
  if (ed.isDestroyed) return;
  const f = findById(ed.state.doc, id);
  if (f && !f.node.attrs.src) ed.view.dispatch(ed.state.tr.delete(f.pos, f.pos + f.node.nodeSize).setMeta('addToHistory', false));
}

/** Заменить изображение рисунка (подпись, ширина, alt сохраняются). */
export async function replaceImage(ctx: Ctx, id: string, file: File) {
  const ed = ctx.editor;
  if (!ed) return;
  pending.set(id, { url: URL.createObjectURL(file), progress: 'Замена…' });
  emit();
  try {
    const { a } = await uploadFile(file);
    const p = pending.get(id);
    pending.delete(id); emit();
    if (p) setTimeout(() => URL.revokeObjectURL(p.url), 5000);
    const f = findById(ed.state.doc, id);
    // место под рисунок (missing: диаграмма/фигуры Word не перенесены) - теперь с изображением
    if (f) ed.view.dispatch(ed.state.tr.setNodeMarkup(f.pos, undefined, { ...f.node.attrs, src: a.name, missing: null }));
    ctx.toast('Изображение заменено, подпись сохранена', 'ok', 2500);
  } catch (e: any) {
    const p = pending.get(id);
    pending.delete(id); emit();
    if (p) URL.revokeObjectURL(p.url);
    ctx.toast('Не удалось заменить: ' + (e?.message || e), 'error', 6000);
  }
}

/** Изображение части k составного рисунка (attrs.images[k]; первая часть - и src рисунка). Превью - usePending(`${id}#${k}`). */
export async function replacePart(ctx: Ctx, id: string, k: number, file: File) {
  const ed = ctx.editor;
  if (!ed) return;
  const key = `${id}#${k}`;
  pending.set(key, { url: URL.createObjectURL(file), progress: 'Загрузка…' });
  emit();
  try {
    const { a } = await uploadFile(file);
    const p = pending.get(key);
    pending.delete(key); emit();
    if (p) setTimeout(() => URL.revokeObjectURL(p.url), 5000);
    const f = findById(ed.state.doc, id);
    const imgs = f && Array.isArray(f.node.attrs.images) ? f.node.attrs.images.map((x: Record<string, unknown>) => ({ ...x })) : [];
    if (f && k >= 0 && k < imgs.length) {
      imgs[k].src = a.name;
      ed.view.dispatch(ed.state.tr.setNodeMarkup(f.pos, undefined, { ...f.node.attrs, images: imgs, missing: null, ...(k === 0 ? { src: a.name } : {}) }));
      ctx.toast(imgs[k].label ? `Часть «${imgs[k].label}» загружена` : 'Часть рисунка загружена', 'ok', 2500);
    }
  } catch (e: any) {
    const p = pending.get(key);
    pending.delete(key); emit();
    if (p) URL.revokeObjectURL(p.url);
    ctx.toast('Не удалось загрузить: ' + (e?.message || e), 'error', 6000);
  }
}

/** Новое изображение на сервере (поворот/обрезка/отражение) -> src рисунка. */
export async function transformImage(ctx: Ctx, id: string, body: { rotate?: number; crop?: { x: number; y: number; w: number; h: number }; flip?: 'h' | 'v'; scale?: number }) {
  const ed = ctx.editor;
  const pid = ctx.store.getState().projectId;
  if (!ed || !pid) return;
  const f = findById(ed.state.doc, id);
  if (!f || !f.node.attrs.src) return;
  const a = await api.post<AssetInfo>(`/api/projects/${encodeURIComponent(pid)}/assets/${encodeURIComponent(f.node.attrs.src)}/transform`, body);
  rememberInfo(a);
  const g = findById(ed.state.doc, id);
  if (g) ed.view.dispatch(ed.state.tr.setNodeMarkup(g.pos, undefined, { ...g.node.attrs, src: a.name }));
}

/** Рисунок из интернета (скачивает сервер). */
export async function insertFromUrl(ctx: Ctx, url: string, at?: number | null) {
  const ed = ctx.editor;
  const pid = ctx.store.getState().projectId;
  if (!ed || !pid) return;
  const a = await api.post<AssetInfo>(`/api/projects/${encodeURIComponent(pid)}/assets/from-url`, { url });
  rememberInfo(a);
  const pos = insertBlocks(ed, { type: 'figure', attrs: { src: a.name, width: null, alt: '' }, content: [{ type: 'caption' }] }, at ?? undefined);
  focusInside(ed, pos);
}

/** Уже загруженное в проект изображение -> рисунок. */
export function insertAsset(ctx: Ctx, name: string) {
  const ed = ctx.editor;
  if (!ed) return;
  const pos = insertBlocks(ed, { type: 'figure', attrs: { src: name, width: null, alt: '' }, content: [{ type: 'caption' }] });
  focusInside(ed, pos);
}
